import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  getAllAkademyClasses,
  getAllAkademySchools,
} from '../../Akademy/AkademySlice';
import {
  Box,
  Button,
  Modal,
  IconButton,
  FormControl,
  InputLabel,
  OutlinedInput,
  InputAdornment,
  CircularProgress,
  withStyles,
  Typography,
} from '@material-ui/core';
import { Search } from '@material-ui/icons';
import BuzzTable from '../../../components/BuzzTable/BuzzTable';
import CustomForm from '../../../components/FormRenderer/CustomForm';
import DeleteIcon from '@material-ui/icons/DeleteOutline';
import useToast from '../../../hooks/useToast';
import useLoader from '../../../hooks/useLoader';
import BuzzSelect from '../../../components/BuzzSelect/BuzzSelect';
import LicenseUploadModal from './LicenseUploadModal';
import usePlanOptions from '../usePlanOptions';
import {
  consumeBuzzLicense,
  consumeBuzzLicenseBulk,
  getAllBuzzLicenses,
  getAllBuzzSubscriptions,
  getAllBuzzUsers,
  getBuzzPlanLicenseTypes,
  revokeBuzzLicense,
} from '../BuzzSlice';
import { getAllBuzzOneSchools } from '../../BuzzOne/BuzzOneSlice';
import BuzzAutoCompleteSingleSelectInput from '../../../components/BuzzAutoCompleteInput/BuzzAutoCompleteSingleSelectInput';

// Stable fallback: a fresh `{}` per render would re-run the Class field effect forever.
const NO_PRODUCT_CLASSES = {};

const CustomOutlinedInput = withStyles((theme) => ({
  root: {
    '& .MuiInputBase-input': {
      padding: '0.75rem',
    },
  },
}))(OutlinedInput);

export default function BuzzLicenses() {
  const dispatch = useDispatch();
  const { handleLoader } = useLoader();
  const { SuccessToast, ErrorToast } = useToast();

  const {
    licenses,
    classes,
    subscriptions,
    users,
    akademySchools,
    buzzOneSchools,
    planLicenseTypes,
    loading,
  } = useSelector(({ akademyReducer, buzz, buzzOne }) => {
    return {
      licenses: buzz.licenses,
      planLicenseTypes: buzz.planLicenseTypes,
      subscriptions: buzz.subscriptions,
      users: buzz.users,
      akademySchools: akademyReducer.schools,
      buzzOneSchools: buzzOne.schools,
      classes: akademyReducer.classes,
      loading: buzz.loading,
    };
  });

  const {
    ecosystems: userTypeOptions,
    licenseTypes,
    productClasses = NO_PRODUCT_CLASSES,
  } = usePlanOptions();

  const [ecosystem, setEcosystem] = useState(null);

  const [showFormModal, setFormModal] = useState(false);
  const [formTemplate, setFormTemplate] = useState(null);
  const [formData, setFormData] = useState({});

  useEffect(() => {
    dispatch(getAllAkademySchools());
    dispatch(getAllBuzzOneSchools());
    dispatch(getAllBuzzLicenses());
    dispatch(getAllAkademyClasses());
  }, []);

  useEffect(() => {
    dispatch(getAllBuzzLicenses({ ecosystem }));
    dispatch(getAllBuzzSubscriptions({ ecosystem }));
    if (ecosystem) dispatch(getAllBuzzUsers({ userType: ecosystem }));
  }, [ecosystem]);

  useEffect(() => {
    setEcosystem(() => formData.ecosystem);
  }, [formData.ecosystem]);

  useEffect(() => {
    dispatch(
      getAllBuzzSubscriptions({ ecosystem, schoolId: formData.schoolId })
    );
  }, [formData.schoolId]);

  const planLicenseTypeOptions = useMemo(
    () =>
      planLicenseTypes.map(({ label, value }) => ({ label, value })),
    [planLicenseTypes]
  );

  const schools = useMemo(() => {
    if (ecosystem === 'BUZZ_ONE') return buzzOneSchools;
    else if (ecosystem === 'AKADEMY') return akademySchools;
    else return [];
  }, [ecosystem]);

  useEffect(() => {
    // if (!users || !subscriptions || !schools) return;

    const userOptions = users.map(({ _id, name, email, mobile }) => {
      return {
        label: `${name ? name + ' |' : ''} ${email} ${
          mobile ? '| ' + mobile : ''
        }`,
        value: _id,
      };
    });

    const subscriptionOptions = subscriptions
      .filter(({ plan }) => plan)
      .map(({ _id, plan }) => {
        return {
          label: `${plan.name} - ${_id}`,
          value: _id,
        };
      });

    const schoolOptions = schools.map(({ _id, name }) => {
      return { label: name, value: _id };
    });

    const formTemplate = [
      {
        id: 'form.ecosystem',
        title: 'Ecosystem',
        slug: 'ecosystem',
        type: 'Select',
        options: userTypeOptions,
        required: true,
      },
      {
        id: 'form.userId',
        title: 'User',
        slug: 'userId',
        type: 'AutoCompleteSelect',
        options: userOptions,
        required: true,
      },
      {
        id: 'form.schoolId',
        title: 'School ',
        slug: 'schoolId',
        type: 'AutoCompleteSelect',
        options: schoolOptions,
        // required: true,
      },
      {
        id: 'form.subscriptionId',
        title: 'Subscription',
        slug: 'subscriptionId',
        type: 'AutoCompleteSelect',
        options: subscriptionOptions,
        required: true,
      },
      {
        id: 'form.licenseType',
        title: 'License Type',
        slug: 'licenseType',
        required: true,
        type: 'AutoCompleteSelect',
        options: planLicenseTypeOptions,
        required: true,
      },
    ];
    setFormTemplate(formTemplate);
  }, [ecosystem, subscriptions, users, schools, userTypeOptions]);

  // A seat's type is whatever the subscription's plan sells — ask buzzOne once one is picked.
  useEffect(() => {
    const selectedSubscription = subscriptions.find(
      ({ _id }) => _id === formData.subscriptionId
    );
    const planId =
      selectedSubscription &&
      (selectedSubscription.planId ||
        (selectedSubscription.plan && selectedSubscription.plan._id));
    if (planId) dispatch(getBuzzPlanLicenseTypes({ planId }));
  }, [formData.subscriptionId]);

  useEffect(() => {
    if (!formTemplate) return;

    if (
      formData.licenseType &&
      !planLicenseTypeOptions.some(({ value }) => value === formData.licenseType)
    )
      setFormData((state) => ({ ...state, licenseType: undefined }));

    setFormTemplate((state) =>
      state.map((form) =>
        form.slug === 'licenseType'
          ? { ...form, options: planLicenseTypeOptions }
          : form
      )
    );
  }, [planLicenseTypeOptions]);

  // The filter spans plans: the selected ecosystem's seats, or every ecosystem's.
  const licenseTypeFilterOptions = useMemo(() => {
    const options = ecosystem
      ? licenseTypes[ecosystem] || []
      : Object.values(licenseTypes).flat();
    return options.filter(
      ({ value }, idx) => options.findIndex((o) => o.value === value) === idx
    );
  }, [ecosystem, licenseTypes]);

  useEffect(() => {
    if (!subscriptions || !formTemplate) return;

    const subscriptionOptions = subscriptions
      .filter(({ plan }) => plan)
      .map(({ _id, plan }) => {
        return {
          label: plan.name,
          value: _id,
        };
      });

    const updatedFormTemplate = formTemplate.map((form) => {
      if (form.slug === 'subscriptionId') form.options = subscriptionOptions;

      return form;
    });
    setFormTemplate(updatedFormTemplate);
  }, [subscriptions]);

  useEffect(() => {
    if (!subscriptions || !formTemplate) return;

    const selectedSubscription = subscriptions.filter(
      ({ _id }) => _id == formData.subscriptionId
    )[0];

    let newFormTemplate = [...formTemplate];

    // Classes are a field of class-bound products only (buzzOne's `productClasses`).
    const hasClassBoundProduct =
      selectedSubscription &&
      selectedSubscription.plan &&
      (selectedSubscription.plan.products || []).some(
        (product) => productClasses[product]
      );

    if (
      hasClassBoundProduct &&
      selectedSubscription.accessClassIds &&
      selectedSubscription.accessClassIds.length
    ) {
      const classesOptions = selectedSubscription.accessClassIds.map(
        ({ _id, name }) => {
          return {
            label: name,
            value: _id,
          };
        }
      );

      let idx = newFormTemplate.findIndex(({ id }) => id === 'classIds');
      if (idx === -1) {
        newFormTemplate.push({
          id: 'classIds',
          title: 'Class',
          slug: 'metadata.accessClassIds',
          type: 'AutoCompleteMultiSelect',
          options: classesOptions,
          required: true,
        });
      }
    } else {
      newFormTemplate = newFormTemplate.filter(({ id }) => id !== 'classIds');
      setFormData({
        ...formData,
        'metadata.accessClassIds': undefined,
      });
    }

    setFormTemplate(newFormTemplate);
    // productClasses too: a subscription picked before the options load must still get its field.
  }, [formData.subscriptionId, formData.licenseType, productClasses]);

  const licensesColumns = [
    // { Header: 'License Id', accessor: '_id' },
    // { Header: 'Subscription Id', accessor: 'subscriptionId' },
    {
      Header: 'User',
      Cell: (val) => {
        const user = val.cell.row.original.user;
        if (!user) return <p>User not found!</p>;
        return (
          <>
            <Typography>{val.cell.row.original.user.name}</Typography>
            <Typography style={{ opacity: 0.5, fontSize: '0.85rem' }}>
              {val.cell.row.original.user.email}
            </Typography>
          </>
        );
      },
    },
    {
      Header: 'License',
      Cell: (val) => {
        const subscription = val.cell.row.original.subscription;
        const licenseType = val.cell.row.original.licenseType;
        const accessClass = val.cell.row.original.accessClass;

        return (
          <>
            <Typography>{licenseType}</Typography>
            <Typography style={{ opacity: 0.5, fontSize: '0.85rem' }}>
              {val.cell.row.original._id}
            </Typography>
            {accessClass && (
              <Typography style={{ opacity: 0.5, fontSize: '0.85rem' }}>
                {accessClass.map(({ code }) => code).join(', ')}
              </Typography>
            )}
          </>
        );
      },
    },
    {
      Header: 'Subscription',
      Cell: (val) => {
        const subscription = val.cell.row.original.subscription;
        if (!subscription) return <p>Subscription not found!</p>;

        const plan = val.cell.row.original.subscription.plan;
        if (!plan) return <p>Subscription Plan not found!</p>;

        return (
          <>
            <Typography>{plan.name}</Typography>
            <Typography style={{ opacity: 0.5, fontSize: '0.85rem' }}>
              {subscription._id}
            </Typography>
            <Typography style={{ opacity: 0.5, fontSize: '0.85rem' }}>
              {subscription.school && subscription.school.name}
            </Typography>
          </>
        );
      },
    },
    {
      Header: 'Action',
      Cell: (val) => {
        const { _id } = val.cell.row.original;

        return (
          <>
            {/* <IconButton onClick={() => handleEdit(_id)}>
              <EditIcon color="secondary" />
            </IconButton> */}
            <IconButton onClick={() => handleDeletion(_id)}>
              <DeleteIcon color="primary" />
            </IconButton>
          </>
        );
      },
    },
  ];

  const [showLicenseUploadModal, setShowLicenseUploadModal] = useState(false);
  const openLicenseUploadModal = () => setShowLicenseUploadModal(true);
  const closeLicenseUploadModal = () => setShowLicenseUploadModal(false);

  const handleBulkLicenseConsumption = (file, subscriptionId) => {
    handleLoader(true);
    dispatch(consumeBuzzLicenseBulk({ file, subscriptionId }))
      .unwrap()
      .then((res) => {
        SuccessToast(res.message);
        dispatch(getAllBuzzLicenses({ ecosystem }));
        closeLicenseUploadModal();
      })
      .catch((error) => {
        ErrorToast(error.message);
      });

    handleLoader(false);
  };

  const handleSave = async () => {
    handleLoader(true);

    const newFormData = JSON.parse(JSON.stringify(formData));

    Object.entries(newFormData).map(([key, value]) => {
      const keys = key.split('.');
      if (keys.length === 2) {
        const outerKey = keys[0];
        const innerKey = keys[1];

        if (!newFormData[outerKey]) newFormData[outerKey] = {};
        newFormData[outerKey][innerKey] = value;

        newFormData[key] = undefined;
      }
    });

    dispatch(consumeBuzzLicense(newFormData))
      .unwrap()
      .then((res) => {
        SuccessToast(res.message);
        dispatch(getAllBuzzLicenses({ ecosystem }));
        setFormModal(false);
        setFormData({});
      })
      .catch((error) => {
        console.error('rejected', error);
        ErrorToast(error.message);
      })
      .finally(() => {
        handleLoader(false);
      });
  };

  const handleDeletion = async (id) => {
    if (!window.confirm('Are you sure ?')) return;

    dispatch(revokeBuzzLicense({ id }))
      .unwrap()
      .then((res) => {
        SuccessToast(res.message);
        dispatch(getAllBuzzLicenses({ ecosystem }));
      })
      .catch((error) => {
        console.error('rejected', error);
        ErrorToast(error.message);
      });
  };

  const [getLicensesQuery, setGetLicensesQuery] = useState({
    userId: undefined,
    classId: undefined,
    licenseType: undefined,
    subscriptionId: undefined,
    searchText: '',
    page: 1,
  });

  const handleQueryChange = async (key, value) => {
    setGetLicensesQuery({ ...getLicensesQuery, [key]: value, page: 1 });
  };

  useEffect(() => {
    dispatch(getAllBuzzLicenses({ ecosystem, ...getLicensesQuery }));
  }, [getLicensesQuery]);

  const handlePageChange = (val) => {
    if (val === getLicensesQuery.page) return;

    setGetLicensesQuery({ ...getLicensesQuery, page: val });
  };

  const [searchText, setSearchText] = useState('');

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      handleQueryChange('searchText', searchText);
    }, 1500);
    return () => clearTimeout(timeoutId);
  }, [searchText]);

  return (
    <Box>
      <Box
        my={2}
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          gap: '2rem',
        }}>
        {' '}
        <Button
          onClick={() => setFormModal(true)}
          variant={'contained'}
          color="primary">
          Consume License
        </Button>
        <Button
          onClick={openLicenseUploadModal}
          variant={'contained'}
          color="primary">
          Bulk Consume License
        </Button>
      </Box>

      <Box
        my={2}
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          gap: '2rem',
        }}>
        <Box style={{ width: '100%' }}>
          {/* <FormControl sx={{ m: 1, width: '100%' }} variant="outlined"> */}
          <CustomOutlinedInput
            id="search-akademy-licenses"
            type={'text'}
            fullWidth
            disabled={loading}
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            endAdornment={
              <InputAdornment position="end">
                {!loading ? (
                  <Search />
                ) : (
                  <Box sx={{ display: 'flex' }}>
                    <CircularProgress size="1.5rem" />
                  </Box>
                )}
              </InputAdornment>
            }
            placeholder="Search"
          />
          {/* </FormControl> */}
        </Box>

        <div style={{ minWidth: '20%' }}>
          <BuzzSelect
            // fullWidth={false}
            name="Ecosystem"
            size={'small'}
            placeholder={'Select Ecosystem'}
            value={ecosystem}
            onChange={(e) => {
              setEcosystem(e.target.value);
              setSearchText('');
            }}
            // label="Select Type"
            // valueKey="_id"
            // labelKey="name"
            options={[
              { label: 'All Ecosystem', value: undefined },
              ...userTypeOptions,
            ]}
          />
        </div>
      </Box>
      <Box
        my={2}
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          gap: '2rem',
        }}>
        <Box style={{ width: '20%' }}>
          <BuzzSelect
            size="small"
            valueKey="value"
            labelKey="label"
            placeholder="License Type"
            options={[
              {
                label: 'None',
                value: undefined,
              },
              ...licenseTypeFilterOptions,
            ]}
            value={getLicensesQuery.licenseType}
            onChange={(e) => {
              handleQueryChange('licenseType', e.target.value);
            }}
          />
        </Box>

        {subscriptions.length > 0 && (
          <Box style={{ width: '40%' }}>
            <BuzzAutoCompleteSingleSelectInput
              size="small"
              valueKey="value"
              labelKey="label"
              placeholder="Subscription"
              options={[
                {
                  label: 'None',
                  value: undefined,
                },
                ...subscriptions.map(({ _id, plan, school }) => {
                  return {
                    label: `${school && school.name} - ${plan && plan.name}`,
                    value: _id,
                  };
                }),
              ]}
              value={getLicensesQuery.subscriptionId}
              onChange={(e) => {
                handleQueryChange('subscriptionId', e.target.value);
              }}
            />
          </Box>
        )}

        {classes.length > 0 && (
          <Box style={{ width: '20%' }}>
            <BuzzSelect
              size="small"
              valueKey="value"
              labelKey="label"
              placeholder="Class"
              options={[
                {
                  label: 'None',
                  value: undefined,
                },
                ...classes.map(({ _id, name }) => {
                  return {
                    label: name,
                    value: _id,
                  };
                }),
              ]}
              value={getLicensesQuery.classId}
              onChange={(e) => {
                handleQueryChange('accessClassId', e.target.value);
              }}
            />
          </Box>
        )}
      </Box>

      <Box my={2}>
        <BuzzTable
          columns={licensesColumns}
          data={licenses}
          isHandlePagination={true}
        />
      </Box>
      <Modal open={showFormModal} onClose={() => setFormModal(false)}>
        <CustomForm
          title={'Consume License'}
          buttonText={'Confirm'}
          formTemplate={formTemplate}
          formData={formData}
          setFormData={(slug, newValue) => {
            setFormData((state) => {
              state[slug] = newValue;
              return state;
            });
            setFormTemplate((state) => {
              return JSON.parse(JSON.stringify(state));
            });
          }}
          mode={'CREATE'}
          handleSave={handleSave}
        />
      </Modal>
      <LicenseUploadModal
        show={showLicenseUploadModal}
        handleCloseModal={closeLicenseUploadModal}
        handleUpload={handleBulkLicenseConsumption}
      />
    </Box>
  );
}
